import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine"
import { SparkLineChart } from "@mui/x-charts/SparkLineChart"
import PropTypes from "prop-types"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { formatDateTime, seriesPeak } from "./statusUtils"

const CHART_HEIGHT = 76
// Only draw the alert line when the data gets close to it; otherwise it would
// squash a healthy chart into the bottom edge.
const LIMIT_VISIBLE_FROM = 0.5

// One metric over the last 24 hours: latest value, peak, filled trend and
// optionally a dashed alert threshold.
export default function TrendTile({
  title,
  points,
  color,
  format,
  limit,
  limitLabel,
  alertWhenPositive = false,
}) {
  const values = points.map((point) => point.v)
  const latest = values.length ? values[values.length - 1] : null
  const peak = seriesPeak(values)
  const showLimit =
    Boolean(limit) && peak !== null && peak >= limit * LIMIT_VISIBLE_FROM
  const latestColor =
    alertWhenPositive && latest > 0 ? "#B42318" : "text.primary"

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        minWidth: 0,
      }}
    >
      <Column gap={0.5}>
        <Row sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <Column>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.2, color: latestColor }}
            >
              {latest === null ? "–" : format(latest)}
            </Typography>
          </Column>
          <Column sx={{ alignItems: "flex-end" }}>
            <Typography variant="caption" color="text.secondary">
              Peak
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {peak === null ? "–" : format(peak)}
            </Typography>
          </Column>
        </Row>

        {values.length ? (
          <SparkLineChart
            data={values}
            height={CHART_HEIGHT}
            colors={[color]}
            area
            curve="monotoneX"
            showTooltip
            showHighlight
            xAxis={{
              scaleType: "point",
              data: points.map((point) => new Date(point.t)),
              valueFormatter: (date) => formatDateTime(date.toISOString()),
            }}
            yAxis={
              showLimit
                ? { min: 0, max: Math.max(limit, peak) * 1.1 }
                : { min: 0 }
            }
            valueFormatter={(value) => (value === null ? "" : format(value))}
          >
            {showLimit ? (
              <ChartsReferenceLine
                y={limit}
                lineStyle={{
                  stroke: "#F79009",
                  strokeDasharray: "4 4",
                  strokeWidth: 1.5,
                }}
              />
            ) : null}
          </SparkLineChart>
        ) : (
          <Box
            sx={{
              height: CHART_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No data
            </Typography>
          </Box>
        )}

        <Row sx={{ justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">
            24h ago
          </Typography>
          {limitLabel ? (
            <Typography
              variant="caption"
              sx={{ color: showLimit ? "#B54708" : "text.secondary" }}
            >
              {limitLabel}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            now
          </Typography>
        </Row>
      </Column>
    </Box>
  )
}

TrendTile.propTypes = {
  title: PropTypes.string.isRequired,
  points: PropTypes.arrayOf(
    PropTypes.shape({ t: PropTypes.string, v: PropTypes.number })
  ).isRequired,
  color: PropTypes.string.isRequired,
  format: PropTypes.func.isRequired,
  limit: PropTypes.number,
  limitLabel: PropTypes.string,
  alertWhenPositive: PropTypes.bool,
}
